import { z } from "zod";

const iso639Regex = /^([a-z]{2,3}(?:-[A-Z]{2})?(?:[-a-zA-Z0-9]{2,})?|C)$/;

export const LanguageCodeSchema = z
  .string()
  .refine((val) => iso639Regex.test(val), {
    message: "Invalid ISO 639 language code",
  });

/**
 * A language a UI is rendered in: exactly a two-letter ISO 639-1 code.
 *
 * Deliberately STRICTER than `LanguageCodeSchema` above, which exists for
 * patient-facing *content* and therefore tolerates region and script subtags
 * (and, being that tolerant, accepts nonsense like `not-a-language`). A UI
 * preference has no use for `fr-CA`: the catalogues are language-only, so a
 * region subtag would either be dropped on the way in or stored as something
 * no client can match.
 *
 * Still open-ended about WHICH two letters, on purpose — the API does not know
 * a client's catalogue list and must not need a redeploy when it grows.
 */
export const UiLanguageSchema = z
  .string()
  .regex(/^[a-z]{2}$/, "Expected a two-letter ISO 639-1 language code");

/** `{ fr: "Bilan initial", en: "Baseline" }` — the shape every piece of
 *  patient-facing text in the contract uses. */
export const TranslationMapSchema = z.record(LanguageCodeSchema, z.string());
export type TranslationMap = z.infer<typeof TranslationMapSchema>;

/**
 * A calendar date with no clock and no zone, `YYYY-MM-DD`.
 *
 * Deliberately not `z.coerce.date()`: a surgery date is a day in the
 * facility's calendar, not an instant. Sending it as an instant means a date
 * entered in Paris arrives as the previous day anywhere the offset is
 * negative, which would silently shift a whole post-op survey series. The
 * clock comes from the schedule's `timeOfDay`, applied in the facility
 * timezone.
 */
export const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected a calendar date, YYYY-MM-DD")
  .refine((value) => {
    const [y, m, d] = value.split("-").map(Number);
    const date = new Date(Date.UTC(y!, m! - 1, d!));
    return (
      date.getUTCFullYear() === y &&
      date.getUTCMonth() === m! - 1 &&
      date.getUTCDate() === d
    );
  }, "Not a real calendar date");
export type CalendarDate = z.infer<typeof CalendarDateSchema>;

/** Local wall-clock time of day, `HH:MM` on a 24h clock. */
export const TimeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Expected a time of day, HH:MM");

/**
 * An IANA zone name (`Europe/Paris`), never a fixed offset: DST means an
 * offset is only correct for half the year, and a survey that opens at 08:00
 * must keep opening at 08:00 across the change.
 */
export const TimeZoneSchema = z.string().refine((value) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}, "Unknown IANA timezone");

export const dateSchema = z
  .string()
  .refine(
    (val) => {
      const date = new Date(val);
      return !isNaN(date.getTime());
    },
    {
      message: "Invalid date format, expected format is YYYY-MM-DD",
    },
  )
  .or(z.date())
  .transform((arg) => new Date(arg));

export const booleanString = z
  .enum(["true", "false"])
  .transform((value) => value === "true");

export const addressSchema = z.object({
  street: z.string(),
  city: z.string(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().length(2),
});

export const patientGenderSchema = z.enum(["MALE", "FEMALE", "OTHER"]);
export type Gender = z.infer<typeof patientGenderSchema>;

/**
 * What a staff member does for a living, and, since the authorization pass,
 * ALSO the key into the baseline capability matrix (`BASELINE_CAPABILITIES`
 * in capabilities.ts). Roles mirror professions one to one by explicit
 * product decision (2026-08-12, reversing the earlier separation): the
 * escape valves for a person whose rights do not match their profession are
 * `User.isAdmin` and per-study grants, never a divergent role list. The
 * `ADMIN` member is still a PROFESSION (a person in administration), not the
 * admin flag: its matrix row grants nothing.
 *
 * CRC is the clinical research coordinator (attaché de recherche clinique).
 */
export const professionTypeSchema = z.enum([
  "DOCTOR",
  "NURSE",
  "RESEARCHER",
  "CRC",
  "QUALITY",
  "MANAGEMENT",
  "ADMIN",
]);
/** Mirrors the Prisma `ProfessionType` enum. */
export type ProfessionType = z.infer<typeof professionTypeSchema>;
